import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, Store, Globe, MessageCircle, ShoppingBag,
  TrendingDown, ThumbsUp, Zap, Shield, Eye, Share2, Users,
  Briefcase, Coffee, PartyPopper, Plane, Gift
} from 'lucide-react';
import MarketingLayout from '../../components/MarketingLayout';
import SEO from '../../components/SEO';
import { TRYON_PAGE_CONFIG, MOMENTS_PAGE_CONFIG } from '../../config/marketingConfig';

// =============================================================================
// IMAGE CONFIGURATION
// =============================================================================
const IMAGES = {
  studioLogo: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260215155909.webp",
  virtualTryOn: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260215191329.webp",
  moments: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260215191405.webp",
  smartSize: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260215191247.webp",
  smartSizeHero: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260215085750.webp"
};

const TRYON = TRYON_PAGE_CONFIG;
const MOMENTS = MOMENTS_PAGE_CONFIG;

const momentIcons = {
  creative_workday: Briefcase,
  unwind: Coffee,
  after_hours: PartyPopper,
  the_escape: Plane,
  gentle_celebration: Gift
};

const tabLabels = {
  creative_workday: 'Workday',
  unwind: 'Unwind',
  after_hours: 'After Hours',
  the_escape: 'Escape',
  gentle_celebration: 'Celebration'
};

// =============================================================================
// SECTION 1: HERO - "Future of Shopping"
// =============================================================================
const HeroSection = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    { label: 'Virtual Try-On', sublabel: 'See it on yourself' },
    { label: 'Moments', sublabel: 'See it in real life' },
    { label: 'Smart Size', sublabel: 'Recommended: M • 92% Fit' }
  ];

  return (
    <section className="min-h-[85vh] flex items-center bg-white py-16 lg:py-20" data-testid="hero-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Logo */}
            <img
              src={IMAGES.studioLogo}
              alt="TryOn Studio"
              className="h-12 mb-6"
              loading="eager"
            />
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-5 leading-tight">
              The future of fashion is{' '}
              <span className="text-blue-600">'see it on me.'</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-500 mb-8 leading-relaxed max-w-lg">
              TryOn Studio™ turns product photos into buying certainty.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Link
                to="/partners/register"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-blue-600 text-white rounded-full font-semibold shadow-lg shadow-blue-500/20 hover:bg-blue-700 hover:shadow-blue-500/30 transition-all"
                data-testid="hero-cta-primary"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-7 py-3.5 bg-slate-100 text-slate-700 rounded-full font-semibold hover:bg-slate-200 transition-all"
                data-testid="hero-cta-secondary"
              >
                Talk to Sales
              </Link>
            </div>

            {/* Chips */}
            <div className="flex flex-wrap gap-3">
              {['Fewer returns', 'Higher confidence', 'Better conversions'].map((chip) => (
                <span
                  key={chip}
                  className="px-4 py-2 bg-blue-50 text-blue-700 text-sm font-medium rounded-full"
                >
                  {chip}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Right: Animated Stepper */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative aspect-square max-w-[500px] mx-auto">
              {/* Step Indicator */}
              <div className="absolute top-0 left-0 right-0 flex justify-center gap-2 mb-4 z-10">
                {steps.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setStep(i)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      step === i
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-white/80 text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Content Area */}
              <div className="mt-16 relative aspect-[4/5] rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 shadow-xl">
                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <motion.div
                      key="tryon"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 flex items-center justify-center gap-3 p-6"
                    >
                      <div className="w-[28%] aspect-[3/4] rounded-xl overflow-hidden shadow-lg">
                        <img src={TRYON.demo.humanImage} alt="Customer" className="w-full h-full object-cover" />
                      </div>
                      <span className="text-slate-300 text-2xl">+</span>
                      <div className="w-[28%] aspect-[3/4] rounded-xl overflow-hidden shadow-lg">
                        <img src={TRYON.demo.dressImage} alt="Dress" className="w-full h-full object-cover" />
                      </div>
                      <ArrowRight className="w-5 h-5 text-blue-500" />
                      <div className="w-[28%] aspect-[3/4] rounded-xl overflow-hidden shadow-xl ring-2 ring-blue-200">
                        <img src={TRYON.demo.outputImage} alt="Result" className="w-full h-full object-cover" />
                      </div>
                    </motion.div>
                  )}
                  {step === 1 && (
                    <motion.div
                      key="moments"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0"
                    >
                      <img
                        src={MOMENTS.moments.unwind.image}
                        alt="Moments"
                        className="w-full h-full object-cover object-top"
                      />
                    </motion.div>
                  )}
                  {step === 2 && (
                    <motion.div
                      key="size"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0"
                    >
                      <img
                        src={IMAGES.smartSizeHero}
                        alt="Smart Size"
                        className="w-full h-full object-cover object-top"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Current Step Label */}
              <p className="text-center mt-4 text-slate-500">{steps[step].sublabel}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// SECTION 2: THE SHIFT
// =============================================================================
const ShiftSection = () => {
  const panels = [
    { label: 'Catalog', image: TRYON.demo.dressImage, opacity: 'opacity-60' },
    { label: 'Self', image: TRYON.demo.outputImage, opacity: '' },
    { label: 'Real life', image: MOMENTS.moments.creative_workday.image, opacity: '' }
  ];

  return (
    <section className="py-20 sm:py-28 bg-slate-50" data-testid="shift-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            The Shift in How People Buy Fashion
          </h2>
          <p className="text-lg text-slate-500">
            Fashion isn't bought. It's imagined before it's purchased.
          </p>
        </motion.div>

        {/* 3-Panel Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="flex items-center justify-center gap-4 sm:gap-6"
        >
          {panels.map((panel, i) => (
            <React.Fragment key={panel.label}>
              <div className="flex-1 max-w-[200px]">
                <div className={`aspect-[3/4] rounded-2xl overflow-hidden shadow-lg ${panel.opacity}`}>
                  <img
                    src={panel.image}
                    alt={panel.label}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <p className={`text-center mt-3 font-medium ${i === 2 ? 'text-blue-600' : 'text-slate-500'}`}>
                  {panel.label}
                </p>
              </div>
              {i < 2 && (
                <ArrowRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
              )}
            </React.Fragment>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// =============================================================================
// SECTION 3: TRYON STUDIO = TRY. EXPERIENCE. DECIDE.
// =============================================================================
const StudioSection = () => {
  const cards = [
    {
      title: 'Virtual Try-On',
      microcopy: 'See it on yourself.',
      image: IMAGES.virtualTryOn,
      color: 'blue'
    },
    {
      title: 'Moments',
      microcopy: 'See it in real life.',
      image: IMAGES.moments,
      color: 'purple'
    },
    {
      title: 'Smart Size',
      microcopy: 'Buy one size. Confidently.',
      image: IMAGES.smartSize,
      color: 'emerald'
    }
  ];

  return (
    <section className="py-20 sm:py-28 bg-white" data-testid="studio-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <img
            src={IMAGES.studioLogo}
            alt="TryOn Studio"
            className="h-[68px] mx-auto mb-4"
            loading="lazy"
          />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            TryOn Studio™ = a new way to sell
          </h2>
          <p className="text-xl text-blue-600 font-medium">
            Try. Experience. Decide.
          </p>
        </motion.div>

        {/* 3 Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {cards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-slate-900 mb-1">{card.title}</h3>
                <p className="text-slate-500 text-sm">{card.microcopy}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// SECTION 4: ONE EXPERIENCE. EVERYWHERE.
// =============================================================================
const OmnichannelSection = () => {
  const tiles = [
    { icon: Store, title: 'Offline stores', desc: 'No trial rooms needed' },
    { icon: Globe, title: 'Online D2C', desc: 'Try before checkout' },
    { icon: MessageCircle, title: 'WhatsApp & Social', desc: 'Sell where customers are' },
    { icon: ShoppingBag, title: 'Marketplaces', desc: 'Stand out instantly' }
  ];

  return (
    <section className="py-20 sm:py-28 bg-slate-50" data-testid="omnichannel-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            One experience. Everywhere.
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {tiles.map((tile, i) => (
            <motion.div
              key={tile.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-blue-100 flex items-center justify-center">
                <tile.icon className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{tile.title}</h3>
              <p className="text-sm text-slate-500">{tile.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// SECTION 5: INTERACTIVE DEMO
// =============================================================================
const InteractiveDemoSection = () => {
  const [mode, setMode] = useState('moments'); // 'tryon' | 'moments'
  const [selectedMoment, setSelectedMoment] = useState('unwind');
  const moments = Object.keys(MOMENTS.moments);

  const currentMoment = MOMENTS.moments[selectedMoment];
  const MomentIcon = momentIcons[selectedMoment];

  return (
    <section className="py-20 sm:py-28 bg-white" data-testid="demo-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            See TryOn Studio in action
          </h2>
          <p className="text-slate-500">
            Select a moment. Watch the same outfit transform.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid lg:grid-cols-[300px_1fr] gap-8 items-start"
        >
          {/* Left: Inputs */}
          <div className="space-y-6">
            {/* Customer + Dress */}
            <div className="bg-slate-50 rounded-2xl p-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Input</p>
              <div className="flex gap-3">
                <div className="flex-1 aspect-[3/4] rounded-xl overflow-hidden shadow">
                  <img src={TRYON.demo.humanImage} alt="Customer" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 aspect-[3/4] rounded-xl overflow-hidden shadow">
                  <img src={TRYON.demo.dressImage} alt="Dress" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>

            {/* Mode Toggle */}
            <div className="bg-slate-100 rounded-full p-1 flex">
              <button
                onClick={() => setMode('tryon')}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                  mode === 'tryon' ? 'bg-white text-slate-900 shadow' : 'text-slate-500'
                }`}
              >
                Try-On
              </button>
              <button
                onClick={() => setMode('moments')}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                  mode === 'moments' ? 'bg-white text-slate-900 shadow' : 'text-slate-500'
                }`}
              >
                Moments
              </button>
            </div>

            {/* Moments Pills */}
            {mode === 'moments' && (
              <div className="flex flex-wrap gap-2">
                {moments.map((key) => {
                  const Icon = momentIcons[key];
                  const isSelected = key === selectedMoment;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedMoment(key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isSelected
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50 shadow-sm'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {tabLabels[key]}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Smart Size Widget */}
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-4 border border-blue-100">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Smart Size</p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-slate-900">M</span>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold">
                  92% Fit
                </span>
              </div>
            </div>
          </div>

          {/* Right: Output */}
          <div className="relative aspect-[3/4] lg:aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl bg-slate-100">
            <AnimatePresence mode="wait">
              {mode === 'tryon' ? (
                <motion.img
                  key="tryon"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  src={TRYON.demo.outputImage}
                  alt="Try-On Result"
                  className="w-full h-full object-cover object-top"
                />
              ) : (
                <motion.img
                  key={selectedMoment}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  src={currentMoment.image}
                  alt={currentMoment.label}
                  className="w-full h-full object-cover object-top"
                />
              )}
            </AnimatePresence>
            
            {/* Label */}
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
              {mode === 'tryon' ? (
                <>
                  <p className="text-xs text-slate-500">Virtual Try-On</p>
                  <p className="font-semibold text-slate-900">Studio Result</p>
                </>
              ) : (
                <>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <MomentIcon className="w-3 h-3" />
                    Moment
                  </p>
                  <p className="font-semibold text-slate-900">{currentMoment.label}</p>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// =============================================================================
// SECTION 6: BUSINESS OUTCOMES
// =============================================================================
const OutcomesSection = () => {
  const outcomes = [
    { icon: TrendingDown, title: 'Lower returns', desc: 'Less size guessing.', color: 'emerald' },
    { icon: ThumbsUp, title: 'Higher conversion', desc: 'More purchase confidence.', color: 'blue' },
    { icon: Zap, title: 'Faster decisions', desc: 'Less back-and-forth.', color: 'amber' }
  ];

  const colorClasses = {
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600' }
  };

  return (
    <section className="py-20 sm:py-28 bg-slate-50" data-testid="outcomes-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
            What businesses get
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {outcomes.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white rounded-2xl p-6 text-center shadow-sm"
            >
              <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl ${colorClasses[item.color].bg} flex items-center justify-center`}>
                <item.icon className={`w-7 h-7 ${colorClasses[item.color].text}`} />
              </div>
              <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
              <p className="text-sm text-slate-500">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// SECTION 7: PRIVACY & TRUST
// =============================================================================
const PrivacySection = () => {
  const points = [
    { icon: Shield, text: 'Secure processing' },
    { icon: Eye, text: 'No training on customer photos' },
    { icon: Share2, text: 'Watermark controls' },
    { icon: Users, text: 'Sharing controlled by partners' }
  ];

  return (
    <section className="py-16 sm:py-20 bg-white" data-testid="privacy-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
            Privacy-first by design
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4"
        >
          {points.map((point) => (
            <div
              key={point.text}
              className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-full"
            >
              <point.icon className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-slate-600">{point.text}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// =============================================================================
// SECTION 8: FINAL CTA
// =============================================================================
const FinalCTASection = () => {
  return (
    <section className="py-20 sm:py-28 bg-gradient-to-b from-slate-50 to-white" data-testid="final-cta-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Build the future shopping journey
          </h2>
          <p className="text-lg text-slate-500 mb-10">
            TryOn Studio™ brings confidence to every channel.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/partners/register"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-full font-semibold text-lg shadow-lg shadow-blue-500/20 hover:bg-blue-700 hover:shadow-blue-500/30 transition-all"
              data-testid="final-cta-primary"
            >
              Start Free
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-slate-100 text-slate-700 rounded-full font-semibold text-lg hover:bg-slate-200 transition-all"
              data-testid="final-cta-secondary"
            >
              Talk to Sales
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// =============================================================================
// MAIN PAGE
// =============================================================================
const TryOnStudioPage = () => {
  return (
    <MarketingLayout lightTheme>
      <SEO 
        title="TryOn Studio™ - Virtual Try-On, Moments & Smart Size | NanoKart.ai"
        description="TryOn Studio™ turns product photos into buying certainty. Virtual Try-On, lifestyle visualization, and AI-powered size recommendations."
        canonical="/tryon-studio"
      />
      
      <HeroSection />
      <ShiftSection />
      <StudioSection />
      <OmnichannelSection />
      <InteractiveDemoSection />
      <OutcomesSection />
      <PrivacySection />
      <FinalCTASection />
    </MarketingLayout>
  );
};

export default TryOnStudioPage;
